import React, { FC, useCallback, useEffect } from 'react';
import { Controller, FieldError, useFieldArray, useForm } from 'react-hook-form';
import { Box, MenuItem, Grid, Button, IconButton, Typography } from '@mui/material';
import {
	CustomFieldDataTypeArray,
	ServiceTypeStatusArray,
	CustomFieldDataTypeEnum,
	CreateCustomFieldDto,
	UpdateCustomFieldDto,
} from '@families-link/shared';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { useTranslation } from 'react-i18next';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { CoreSelect } from '../../../_core/components/_ui/core-select';
import CloseIcon from '@mui/icons-material/Close';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { AddIcon } from '../../../_core/components/icons';

export type AddCustomFieldFormProps = CoreModalProps & {
	resolver: any;
	formError?: any;
	defaultValues?: any;
	formTitle?: string;
	isEditMode?: boolean;
	isDefault?: boolean;
	submitAction(customFields: CreateCustomFieldDto | UpdateCustomFieldDto): Promise<void>;
	closeModal(): void;
};

export const AddCustomFieldForm: FC<AddCustomFieldFormProps> = ({
	closeModal,
	submitAction,
	formError,
	defaultValues,
	resolver,
	formTitle,
	isEditMode = false,
	isDefault = false,
	...modalProps
}) => {
	const [t] = useTranslation();

	const {
		register,
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isDirty, isLoading, isSubmitting, isSubmitted, isValid },
	} = useForm({
		defaultValues,
		resolver,
		mode: 'all',
	});

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const fieldType = watch('dataType');

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'options',
	});

	useEffect(() => {
		if (fieldType !== CustomFieldDataTypeEnum.Options && fieldType !== CustomFieldDataTypeEnum.MultiSelect) {
			setValue('options', [{ text: '' }]);
		}
	}, [fieldType, setValue]);

	const handleFormSubmit = useCallback(
		async (values: CreateCustomFieldDto | UpdateCustomFieldDto) => {
			const newValues = { ...values };
			await submitAction(newValues);
		},
		[submitAction]
	);

	return (
		<CoreModal
			onClose={closeModal}
			modalWidth={500}
			{...modalProps}
			bodySx={{ py: 0, mb: 5 }}
			paperSx={{ width: '100%' }}
		>
			<Typography
				variant="h5"
				sx={mergeSx(mainTitleModalWindowStyles, {
					position: 'sticky',
					top: 0,
					zIndex: 1,
					backgroundColor: (theme) => theme.colors.white,
				})}
			>
				{formTitle}
			</Typography>
			<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
					<CoreTextField
						label={t('custom-fields.forms.labels.name')}
						{...register('name')}
						requiredMark
						fullWidth
						placeholder={t('custom-fields.forms.placeholders.name')}
						disabled={isDefault}
						error={!!errors['name']}
						helperText={errors['name'] ? (errors['name'] as FieldError).message : undefined}
					/>
					{!isDefault && (
						<Controller
							name={'engagementType'}
							control={control}
							render={({ field }) => (
								<CoreSelect
									{...field}
									{...register('engagementType')}
									label={t('custom-fields.forms.labels.engagement-type')}
									requiredMark
									placeholder={t('custom-fields.forms.placeholders.select-option')}
									error={!!errors['engagementType']}
									helperText={errors['engagementType'] ? (errors['engagementType'] as FieldError).message : undefined}
									fullWidth
								>
									{ServiceTypeStatusArray?.map((el: any) => (
										<MenuItem key={el} value={el}>
											{el}
										</MenuItem>
									))}
								</CoreSelect>
							)}
						/>
					)}
					<Controller
						name={'isRequired'}
						control={control}
						render={({ field }) => (
							<CoreSelect
								{...field}
								{...register('isRequired')}
								label={t('custom-fields.forms.labels.verification-type')}
								requiredMark
								placeholder={t('custom-fields.forms.placeholders.select-option')}
								error={!!errors['isRequired']}
								helperText={errors['isRequired'] ? (errors['isRequired'] as FieldError).message : undefined}
								fullWidth
							>
								<MenuItem value="true">{t('custom-fields.isRequired')}</MenuItem>
								<MenuItem value="false">{t('custom-fields.optional')}</MenuItem>
							</CoreSelect>
						)}
					/>
					<Controller
						name={'dataType'}
						control={control}
						render={({ field }) => {
							const currentType = field.value as CustomFieldDataTypeEnum;

							const isDisabled = (option: CustomFieldDataTypeEnum) => {
								if (!isEditMode || isDefault) return false;

								switch (currentType) {
									case CustomFieldDataTypeEnum.ShortText:
										return ![CustomFieldDataTypeEnum.ShortText, CustomFieldDataTypeEnum.LongText].includes(option);

									case CustomFieldDataTypeEnum.LongText:
										return ![CustomFieldDataTypeEnum.LongText, CustomFieldDataTypeEnum.ShortText].includes(option);

									case CustomFieldDataTypeEnum.Options:
									case CustomFieldDataTypeEnum.MultiSelect:
										return true;

									case CustomFieldDataTypeEnum.File:
										return true;

									default:
										return false;
								}
							};

							const shouldDisableSelect = isDefault || (isEditMode && currentType === CustomFieldDataTypeEnum.File);

							return (
								<CoreSelect
									{...field}
									{...register('dataType')}
									label={t('custom-fields.forms.labels.data-type')}
									error={!!errors['dataType']}
									helperText={errors['dataType'] ? (errors['dataType'] as FieldError).message : undefined}
									requiredMark
									placeholder={t('custom-fields.forms.placeholders.select-option')}
									disabled={shouldDisableSelect}
									fullWidth
								>
									{CustomFieldDataTypeArray.map((fieldType) => (
										<MenuItem key={fieldType} value={fieldType} disabled={isDisabled(fieldType)}>
											{fieldType}
										</MenuItem>
									))}
								</CoreSelect>
							);
						}}
					/>

					{(fieldType === CustomFieldDataTypeEnum.Options || fieldType === CustomFieldDataTypeEnum.MultiSelect) && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<Typography
								variant="body1"
								sx={{ fontSize: '12px', fontWeight: 400, color: (theme) => theme.colors.gray }}
							>
								Options
							</Typography>
							{fields?.map((field, index) => (
								<Box key={field.id} sx={{ display: 'flex', alignItems: 'center' }}>
									<Controller
										name={`options.${index}.text`}
										control={control}
										render={({ field }) => (
											<CoreTextField
												{...field}
												error={!!(errors['options'] as any)?.[index]?.text}
												placeholder={t('custom-fields.forms.placeholders.option')}
												fullWidth
												controlSx={{
													'.MuiFormLabel-root': { fontSize: '12px' },
												}}
											/>
										)}
									/>
									<IconButton
										edge="end"
										onClick={() => {
											remove(index);
										}}
										sx={{ color: (theme) => theme.colors.black, fontWeight: 600 }}
									>
										<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
											<path
												d="M13 3L3 13"
												stroke="#0A0A0A"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
											<path
												d="M3 3L13 13"
												stroke="#0A0A0A"
												stroke-width="2"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									</IconButton>
								</Box>
							))}
							<Button
								variant="text"
								onClick={() => {
									append({ text: '' });
								}}
								sx={{
									display: 'flex',
									alignItems: 'center',
									textTransform: 'capitalize',
									color: (theme) => theme.colors.lightBlue,
									fontSize: '14px',
								}}
							>
								<AddIcon sx={{ fontSize: '12px', color: (theme) => theme.colors.lightBlue, mr: '6px' }} />
								Add Option
							</Button>
						</Box>
					)}
				</Box>

				<Box
					sx={{
						display: 'flex',
						gap: 3,
						justifyContent: 'flex-end',
						mt: 5,
						position: 'sticky',
						bottom: 0,
						zIndex: 1,
						backgroundColor: (theme) => theme.colors.white,
					}}
				>
					<CoreButton variant="secondary" onClick={closeModal} sx={buttonModalWindowStyles}>
						{t('providers.close')}
					</CoreButton>
					<CoreButton
						type="submit"
						contraModeStyleButton
						loading={loadingSubmit}
						disabled={disableSubmit}
						sx={buttonModalWindowStyles}
					>
						{isEditMode ? t('custom-fields.save') : t('custom-fields.create')}
					</CoreButton>
				</Box>
			</Box>
		</CoreModal>
	);
};
